import { lazy, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, Guitar, Heart, Music, Download, Smartphone, Play, FileText, Users, MessageCircle, Newspaper, Star, Quote, Headphones, ChevronDown, Flame, Sparkles, HelpCircle, Eye, ScrollText, MonitorSmartphone, Search, Volume2 } from "lucide-react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect, useState, useMemo } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FavoriteButton from "@/components/FavoriteButton";
import WhatsAppButton from "@/components/WhatsAppButton";
import EmailCapture from "@/components/EmailCapture";
import TestimonialsSection from "@/components/TestimonialsSection";
import { useHinos, CATEGORIAS_HINOS } from "@/hooks/useHinos";
import { useAuth } from "@/hooks/useAuth";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useFavorites } from "@/hooks/useFavorites";
import { useHinoHistory } from "@/hooks/useHinoHistory";
import { useBlogPosts, BLOG_CATEGORIES } from "@/hooks/useBlogPosts";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import storeHeroBg from "@/assets/store-hero-bg.webp";
const ChordChallengeWidget = lazy(() => import("@/components/ChordChallengeWidget"));
import guitarColorfulBg from "@/assets/guitar-colorful-bg.avif";
import guitarForestBg from "@/assets/guitar-forest-bg.avif";


const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" as const }
  }
};

/* Typing animation hook */
const TYPING_PHRASES = [
  "O maior acervo organizado de hinos cristãos com cifras simplificadas.",
  "Letras e cifras para músicos e igrejas de todo o Brasil.",
  "Hinos avulsos com cifras fáceis para violão e guitarra.",
  "Toque e cante louvores com cifras simplificadas e gratuitas.",
];

function useTypingAnimation(phrases: string[], typingSpeed = 45, deletingSpeed = 25, pauseDuration = 2500) {
  const [displayText, setDisplayText] = useState("");
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentPhrase = phrases[phraseIndex];
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        setDisplayText(currentPhrase.substring(0, displayText.length + 1));
        if (displayText.length + 1 === currentPhrase.length) {
          setTimeout(() => setIsDeleting(true), pauseDuration);
        }
      } else {
        setDisplayText(currentPhrase.substring(0, displayText.length - 1));
        if (displayText.length === 0) {
          setIsDeleting(false);
          setPhraseIndex((prev) => (prev + 1) % phrases.length);
        }
      }
    }, isDeleting ? deletingSpeed : typingSpeed);
    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, phraseIndex, phrases, typingSpeed, deletingSpeed, pauseDuration]);

  return displayText;
}

const FAQ_ITEMS = [
  { question: "O que é o Louve Hinos Avulsos?", answer: "É uma plataforma que organiza hinos avulsos com letras completas e cifras atualizadas para músicos, instrumentistas e adoradores." },
  { question: "Os hinos possuem cifras atualizadas?", answer: "Sim, os hinos disponíveis possuem cifras organizadas e revisadas para facilitar o uso por músicos de todos os níveis." },
  { question: "Posso acessar pelo celular?", answer: "Sim, o site é totalmente adaptado para dispositivos móveis. Você também pode instalar como aplicativo para acesso offline." },
  { question: "O Louve é totalmente gratuito?", answer: "Sim! O Louve Hinos Avulsos é 100% gratuito. Você pode acessar todas as letras, cifras e ferramentas sem nenhum custo." },
  { question: "Como posso sugerir um hino novo?", answer: "Você pode nos enviar sugestões pelo WhatsApp ou e-mail. Estamos sempre adicionando novos hinos com base nos pedidos da comunidade." },
  { question: "As cifras são simplificadas?", answer: "Sim! Todas as cifras são adaptadas para facilitar o aprendizado, ideais tanto para iniciantes quanto para músicos experientes." }
];

const CATEGORY_ICONS: Record<string, { icon: React.ReactNode; description: string }> = {
  avulsos: { icon: <Music className="h-7 w-7" />, description: "Acervo completo de hinos avulsos organizados" },
  com_cifra: { icon: <Guitar className="h-7 w-7" />, description: "Hinos com cifras para violão e outros instrumentos" },
  iniciantes: { icon: <Sparkles className="h-7 w-7" />, description: "Cifras fáceis perfeitas para quem está começando" },
  mais_acessados: { icon: <Flame className="h-7 w-7" />, description: "Os hinos mais populares da comunidade" }
};

const Index = () => {
  usePageTitle(undefined, {
    description: "Cifras de hinos avulsos para violão com letras completas, transposição de tom, rolagem automática e PDF grátis. O maior acervo de cifras cristãs para músicos da igreja.",
    keywords: "cifra de hinos avulsos, hinos avulsos cifra violão, cifras cristãs para violão, louve hinos avulsos",
  });
  const { data: hinos, isLoading } = useHinos();
  const { user } = useAuth();
  const { favorites } = useFavorites();
  const { history } = useHinoHistory();
  const { data: blogPosts } = useBlogPosts(true);
  const { data: siteSettings } = useSiteSettings();
  const navigate = useNavigate();

  const hinoCountLabel = siteSettings?.hino_count || "+300";

  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImageY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  // Search
  const [searchTerm, setSearchTerm] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const searchResults = useMemo(() => {
    if (!hinos || !searchTerm.trim()) return [];
    const term = searchTerm.toLowerCase();
    return hinos.filter(h =>
      h.titulo.toLowerCase().includes(term) || h.numero.toString().includes(term)
    ).slice(0, 6);
  }, [hinos, searchTerm]);

  const mostViewedHinos = useMemo(() =>
    hinos?.slice().sort((a, b) => (b.visualizacoes || 0) - (a.visualizacoes || 0)).slice(0, 8),
    [hinos]
  );

  const recentHinos = useMemo(() =>
    hinos?.slice().sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).slice(0, 6),
    [hinos]
  );

  const hinosComAudio = useMemo(() =>
    hinos?.filter(h => h.audio_url && h.audio_url.trim() !== "").slice(0, 6),
    [hinos]
  );

  const hinosFaceis = useMemo(() => {
    if (!hinos) return [];
    return hinos.filter(h => {
      if (!h.cifra) return false;
      const chords = [...new Set((h.cifra.match(/\b([A-G][#b]?(?:m|maj|min|dim|aug|sus|add|7|9|11|13|M)*)/g) || []))];
      const complexCount = chords.filter(c => /dim|aug|sus|add|maj|9|11|13/.test(c)).length;
      return chords.length <= 5 && complexCount === 0;
    }).slice(0, 6);
  }, [hinos]);

  return (
    <div className="min-h-screen bg-background" role="main">
      <Header />

      {/* ===== 1. HERO SECTION ===== */}
      <section ref={heroRef} className="relative flex items-center min-h-[85vh] md:min-h-[100vh] overflow-hidden -mt-20">
        <motion.div className="absolute inset-0 w-full h-full" style={{ y: heroImageY }}>
          <img src={storeHeroBg} alt="Violão acústico cinematográfico" className="w-full h-full object-cover object-center scale-110" loading="eager" fetchPriority="high" width={1920} height={1080} decoding="async" />
        </motion.div>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(11,28,45,0.88) 0%, rgba(11,28,45,0.7) 45%, rgba(11,28,45,0.35) 100%)' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background" />

        {/* Floating particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(5)].map((_, i) => (
            <motion.div key={i} className="absolute w-1 h-1 rounded-full bg-primary/30" style={{ left: `${10 + i * 12}%`, top: `${20 + i * 12}%` }}
              animate={{ y: [0, -40, 0], opacity: [0.2, 0.6, 0.2], scale: [1, 1.5, 1] }}
              transition={{ duration: 4 + i, repeat: Infinity, delay: i * 0.8, ease: "easeInOut" }}
            />
          ))}
        </div>
        <div className="absolute top-1/2 left-[25%] -translate-x-1/2 -translate-y-1/2 w-[500px] h-[400px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

        <motion.div className="container mx-auto relative z-10 px-6 sm:px-8 pt-32 pb-20 max-w-6xl" style={{ opacity: heroOpacity }}>
          <motion.div className="max-w-2xl" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
            <motion.div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/10 rounded-full px-5 py-2 mb-8" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.3 }}>
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-sm font-medium tracking-wide text-white/90">🎶 Louve com excelência</span>
            </motion.div>

            <motion.h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight leading-[1.1] text-primary" style={{ fontFamily: 'var(--font-serif)', textShadow: '0 0 30px hsl(var(--primary) / 0.25), 0 2px 8px rgba(0,0,0,0.5)' }} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
              Letras e Cifras de Hinos para Louvar com Excelência
            </motion.h1>

            <motion.p className="text-base sm:text-lg md:text-xl mb-6 font-light tracking-wide leading-relaxed max-w-xl text-white/80" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5 }}>
              Toque, cante e viva o louvor com cifras simplificadas e gratuitas.
            </motion.p>

            <motion.div className="flex flex-wrap gap-x-6 gap-y-2 mb-10" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.6 }}>
              {["Cifras simplificadas", "Áudio dos hinos", "Ferramentas para músicos", "100% gratuito"].map((item) => (
                <span key={item} className="flex items-center gap-2 text-sm text-white/70">
                  <span className="w-5 h-5 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-primary text-xs">✓</span>
                  {item}
                </span>
              ))}
            </motion.div>

            <motion.div className="flex flex-col sm:flex-row gap-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.7 }}>
              <Link to="/hinos-avulsos" className="group relative inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-primary to-[hsl(45,96%,56%)] text-primary-foreground px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-[0_8px_32px_hsl(38,92%,50%,0.3)] hover:shadow-[0_12px_40px_hsl(38,92%,50%,0.5)]">
                <FileText className="h-5 w-5" />
                Ver Hinos Agora
                <ArrowRight className="h-4 w-4 opacity-0 -ml-2 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
              </Link>
              <Link to="/ouvir-hinos" className="group inline-flex items-center justify-center gap-2.5 bg-white/5 backdrop-blur-xl px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white/15 transition-all duration-300 hover:scale-105 border border-white/15 hover:border-white/30 text-white/90">
                <Play className="h-5 w-5 text-primary" />
                Ouvir Hinos
              </Link>
            </motion.div>

            <motion.div className="flex items-center gap-6 mt-12 text-white/40" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 1.1 }}>
              <div className="flex items-center gap-2 text-sm"><Music className="h-4 w-4 text-primary/60" /><span>{hinoCountLabel} hinos</span></div>
              <div className="w-px h-4 bg-white/20" />
              <div className="flex items-center gap-2 text-sm"><Guitar className="h-4 w-4 text-primary/60" /><span>Cifras simplificadas</span></div>
              <div className="w-px h-4 bg-white/20 hidden sm:block" />
              <div className="hidden sm:flex items-center gap-2 text-sm"><Heart className="h-4 w-4 text-primary/60" /><span>100% gratuito</span></div>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* ===== 2. BUSCA GRANDE (Cifra Club style) ===== */}
      <section className="relative z-20 -mt-8 px-4 pb-8">
        <div className="container mx-auto max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="relative">
            <div className={`relative bg-card rounded-2xl border shadow-2xl transition-all duration-300 ${searchFocused ? 'border-primary/40 shadow-primary/10' : 'border-border'}`}>
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-6 w-6 text-primary" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                placeholder="🔎 Buscar hino por título ou número..."
                className="w-full pl-14 pr-6 py-5 rounded-2xl bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none text-lg"
                autoComplete="off"
              />
            </div>
            {/* Search Results Dropdown */}
            {searchFocused && searchTerm.trim().length > 0 && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-2xl z-50 overflow-hidden max-h-80 overflow-y-auto">
                {searchResults.map((hino) => (
                  <Link key={hino.id} to={`/hino/${hino.id}`} className="flex items-center gap-3 px-5 py-3.5 hover:bg-primary/5 transition-colors border-b border-border/50 last:border-b-0 group">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                      <Music className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">{hino.titulo}</p>
                      <p className="text-xs text-muted-foreground">Hino nº {hino.numero}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                  </Link>
                ))}
                <Link to="/hinos-avulsos" className="flex items-center justify-center gap-2 px-5 py-3 bg-muted/50 text-primary text-sm font-medium hover:bg-primary/5 transition-colors">
                  Ver todos os hinos <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            )}
          </motion.div>

          {/* Quick access pills */}
          <div className="flex items-center gap-2 mt-4 overflow-x-auto scrollbar-hide pb-1">
            {[
              { label: "🔥 Populares", action: () => navigate("/hinos-avulsos?categoria=mais_acessados") },
              { label: "🎸 Com Cifra", action: () => navigate("/hinos-avulsos?categoria=com_cifra") },
              { label: "⭐ Fáceis", action: () => navigate("/hinos-avulsos?categoria=iniciantes") },
              { label: "🎧 Com Áudio", action: () => navigate("/ouvir-hinos") },
              { label: "📄 Baixar PDF", action: () => navigate("/baixar-cifras") },
            ].map((pill) => (
              <button key={pill.label} onClick={pill.action} className="whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium bg-card border border-border text-foreground/70 hover:text-primary hover:border-primary/30 transition-all duration-300 hover:shadow-md">
                {pill.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA HINO PROFISSIONAL — Premium Banner ===== */}
      <section className="py-10 px-4">
        <div className="container mx-auto max-w-4xl">
          <Link to="/hino-profissional" className="block group" onMouseEnter={() => import("./HinoProfissional")}>
            <motion.div
              className="relative rounded-3xl overflow-hidden border border-[hsl(45,96%,56%)]/15 hover:border-[hsl(45,96%,56%)]/40 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl hover:shadow-[hsl(45,96%,56%)]/10"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              {/* Dark gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-[hsl(220,40%,7%)] via-[hsl(225,30%,13%)] to-[hsl(220,25%,10%)]" />
              {/* Decorative glow */}
              <div className="absolute -top-20 -right-20 w-60 h-60 bg-[hsl(45,96%,56%)]/5 rounded-full blur-3xl" />
              <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-[hsl(220,80%,60%)]/5 rounded-full blur-3xl" />
              {/* Sound wave decoration */}
              <div className="absolute right-6 top-1/2 -translate-y-1/2 hidden md:flex gap-1 opacity-20">
                {[16, 28, 20, 36, 24, 32, 18, 28, 14].map((h, i) => (
                  <div key={i} className="w-1 rounded-full bg-[hsl(45,96%,56%)]" style={{ height: `${h}px`, animationDelay: `${i * 0.1}s` }} />
                ))}
              </div>

              <div className="relative z-10 p-6 sm:p-8 md:p-10 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                {/* Icon */}
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[hsl(45,96%,56%)] to-[hsl(35,96%,45%)] flex items-center justify-center shrink-0 shadow-lg shadow-[hsl(45,96%,56%)]/20 group-hover:scale-110 transition-transform duration-300">
                  <Headphones className="h-7 w-7 text-[hsl(220,40%,7%)]" />
                </div>

                {/* Text */}
                <div className="flex-1 space-y-2">
                  <h3 className="text-xl sm:text-2xl font-bold text-white leading-tight">
                    🎧 Transforme seu hino em uma produção profissional
                  </h3>
                  <p className="text-white/50 text-sm sm:text-base leading-relaxed max-w-xl">
                    Envie seu áudio ou letra e receba uma música com qualidade de estúdio, pronta para tocar em igrejas, eventos e redes sociais.
                  </p>
                  <div className="flex flex-wrap gap-4 pt-1">
                    <span className="flex items-center gap-1.5 text-xs text-[hsl(45,96%,56%)]/80">
                      <Star className="h-3.5 w-3.5" /> Mais de 100 hinos produzidos
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-[hsl(45,96%,56%)]/80">
                      <Sparkles className="h-3.5 w-3.5" /> Entrega rápida e segura
                    </span>
                  </div>
                </div>

                {/* CTA Button */}
                <div className="shrink-0 self-center">
                  <div className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[hsl(45,96%,56%)] to-[hsl(35,96%,48%)] text-[hsl(220,40%,7%)] font-bold text-sm sm:text-base px-6 py-3 shadow-lg shadow-[hsl(45,96%,56%)]/20 group-hover:shadow-xl group-hover:shadow-[hsl(45,96%,56%)]/30 group-hover:scale-105 transition-all duration-300">
                    CRIAR MEU HINO AGORA
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </motion.div>
          </Link>
        </div>
      </section>


      {/* ===== DESAFIO DOS ACORDES WIDGET ===== */}
      <section className="pb-8 px-4">
        <div className="container mx-auto max-w-3xl">
          <Suspense fallback={null}>
            <ChordChallengeWidget />
          </Suspense>
        </div>
      </section>

      {/* ===== 3. HINOS MAIS ACESSADOS (Ranking) ===== */}
      <section className="py-14 md:py-20 px-4 bg-background">
        <div className="container mx-auto max-w-3xl">
          <motion.div className="text-center mb-10" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <motion.div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-5" initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
              <Flame className="h-3.5 w-3.5 text-primary" />
              <span className="text-primary text-xs font-semibold tracking-wider uppercase">Popular</span>
            </motion.div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'var(--font-serif)' }}>
              🔥 Hinos Mais Tocados
            </h2>
            <p className="text-muted-foreground mt-3">Os hinos mais populares da comunidade.</p>
          </motion.div>

          {isLoading ? (
            <div className="space-y-2">{[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
          ) : !mostViewedHinos || mostViewedHinos.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-2xl border border-border"><p className="text-muted-foreground">Nenhum hino cadastrado ainda.</p></div>
          ) : (
            <motion.div className="bg-card rounded-2xl overflow-hidden border border-border shadow-sm" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              {mostViewedHinos.map((hino, index) => (
                <motion.div key={hino.id} variants={itemVariants}>
                  <Link to={`/hino/${hino.id}`} className={`group flex items-center justify-between px-5 py-4 hover:bg-primary/5 transition-all duration-300 ${index !== mostViewedHinos.length - 1 ? 'border-b border-border' : ''}`}>
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <span className={`text-lg font-bold w-7 text-center shrink-0 ${index < 3 ? 'text-primary' : 'text-muted-foreground/40'}`}>
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-foreground group-hover:text-primary transition-colors">{hino.titulo}</h3>
                        <p className="text-muted-foreground text-sm mt-0.5 flex items-center gap-2">
                          <span className="text-xs">Hino nº {hino.numero}</span>
                          <span className="flex items-center gap-1 text-xs"><Eye className="h-3 w-3" /> {hino.visualizacoes || 0}</span>
                        </p>
                      </div>
                    </div>
                    <span className="text-primary text-sm font-medium ml-4 whitespace-nowrap flex items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                      Ver Cifra <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}

          <div className="text-center mt-8">
            <Link to="/hinos-avulsos" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors">
              Ver todos os hinos <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== 4. CATEGORIAS ===== */}
      <section className="py-16 md:py-24 px-4 bg-muted/30 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-primary/3 rounded-full blur-[120px] pointer-events-none" />
        <div className="container mx-auto max-w-5xl relative z-10">
          <motion.div className="text-center mb-14" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <motion.div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-5" initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              <span className="text-primary text-xs font-semibold tracking-wider uppercase">Categorias</span>
            </motion.div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground" style={{ fontFamily: 'var(--font-serif)' }}>Explore por Categoria</h2>
            <p className="text-muted-foreground mt-3 max-w-lg mx-auto text-sm md:text-base">Encontre rapidamente o que precisa navegando pelas categorias.</p>
          </motion.div>

          <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }}>
            {CATEGORIAS_HINOS.map((cat) => {
              const meta = CATEGORY_ICONS[cat.value];
              return (
                <motion.div key={cat.value} variants={itemVariants}>
                  <Link to={`/hinos-avulsos?categoria=${cat.value}`} className="group relative block rounded-2xl bg-card border border-border p-6 md:p-7 text-center h-full overflow-hidden transition-all duration-500 hover:border-primary/40 hover:shadow-[0_8px_30px_hsl(38,92%,50%,0.1)]">
                    <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                    <div className="relative z-10">
                      <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/15 flex items-center justify-center mx-auto mb-4 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-500 group-hover:scale-110 group-hover:shadow-[0_8px_24px_hsl(38,92%,50%,0.3)] group-hover:rotate-3">
                        {meta?.icon || <Music className="h-7 w-7" />}
                      </div>
                      <h3 className="font-bold text-foreground text-sm md:text-base mb-1.5 group-hover:text-primary transition-colors duration-300" style={{ fontFamily: 'var(--font-serif)' }}>{cat.label}</h3>
                      <p className="text-muted-foreground text-xs leading-relaxed hidden md:block">{meta?.description || "Explore os hinos desta categoria"}</p>
                      <div className="mt-4 inline-flex items-center gap-1 text-xs text-primary/60 group-hover:text-primary transition-colors duration-300">
                        <span>Explorar</span>
                        <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform duration-300" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ===== 5. FERRAMENTAS PARA MÚSICOS ===== */}
      <section className="py-14 md:py-20 px-4 bg-background overflow-hidden">
        <div className="container mx-auto max-w-5xl">
          <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <motion.div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-5" initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
              <Guitar className="h-3.5 w-3.5 text-primary" />
              <span className="text-primary text-xs font-semibold tracking-wider uppercase">Para Músicos</span>
            </motion.div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'var(--font-serif)' }}>Ferramentas para Músicos</h2>
            <p className="text-muted-foreground mt-3 max-w-lg mx-auto">Tudo o que você precisa para tocar e louvar com excelência.</p>
          </motion.div>
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }}>
            {[
              { icon: <Guitar className="h-8 w-8" />, title: "Afinador de Violão", desc: "Afine seu violão direto pelo celular com medidor profissional de precisão.", link: "/afinador", cta: "Acessar Afinador" },
              { icon: <Download className="h-8 w-8" />, title: "Baixar Cifras em PDF", desc: "Exporte cifras profissionais em PDF para imprimir e levar para o ensaio.", link: "/baixar-cifras", cta: "Baixar Cifras" },
              { icon: <ScrollText className="h-8 w-8" />, title: "Rolagem Automática", desc: "Ative a rolagem automática da cifra enquanto toca, sem precisar usar as mãos.", link: "/hinos-avulsos", cta: "Ver Hinos" },
              { icon: <MonitorSmartphone className="h-8 w-8" />, title: "Modo Músico", desc: "A tela não apaga enquanto você toca. Fonte grande e interface limpa.", link: "/hinos-avulsos", cta: "Experimentar" },
              { icon: <Smartphone className="h-8 w-8" />, title: "Instale o App", desc: "Tenha acesso offline direto no seu celular, como um app nativo.", link: "/instalar", cta: "Instalar Grátis" },
              { icon: <Headphones className="h-8 w-8" />, title: "Ouvir Hinos", desc: "Ouça os hinos com player completo, controle de faixa e reprodução contínua.", link: "/ouvir-hinos", cta: "Ouvir Agora" }
            ].map((tool, i) => (
              <motion.div key={i} className="group text-center p-8 md:p-10 rounded-2xl bg-card border border-border hover:border-primary/40 shadow-md hover:shadow-xl transition-all duration-300" variants={itemVariants} whileHover={{ scale: 1.03, y: -5 }}>
                <div className="w-16 h-16 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-6 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-primary/20">
                  {tool.icon}
                </div>
                <h3 className="font-bold text-xl text-foreground mb-3">{tool.title}</h3>
                <div className="w-12 h-px bg-primary/40 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">{tool.desc}</p>
                <Link to={tool.link} className="text-primary hover:text-primary/80 text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  {tool.cta} <ArrowRight className="h-3 w-3" />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== 6. HINOS FÁCEIS ===== */}
      {hinosFaceis.length > 0 && (
        <section className="py-14 md:py-20 px-4 bg-muted/30">
          <div className="container mx-auto max-w-3xl">
            <motion.div className="text-center mb-10" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <motion.div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-5" initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
                <Star className="h-3.5 w-3.5 text-primary" />
                <span className="text-primary text-xs font-semibold tracking-wider uppercase">Iniciantes</span>
              </motion.div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'var(--font-serif)' }}>🎸 Hinos Fáceis para Iniciantes</h2>
              <p className="text-muted-foreground mt-3">Cifras simples, perfeitas para quem está aprendendo violão.</p>
            </motion.div>

            <motion.div className="grid grid-cols-1 sm:grid-cols-2 gap-4" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              {hinosFaceis.map((hino) => (
                <motion.div key={hino.id} variants={itemVariants}>
                  <Link to={`/hino/${hino.id}`} className="group flex items-center gap-4 p-4 rounded-2xl bg-card border border-border hover:border-primary/30 hover:shadow-lg transition-all duration-300">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                      <Sparkles className="h-5 w-5 text-primary group-hover:text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors truncate">{hino.titulo}</h3>
                      <p className="text-muted-foreground text-xs mt-0.5">Hino nº {hino.numero} • Fácil</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary shrink-0 transition-colors" />
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* ===== 7. HINOS COM ÁUDIO ===== */}
      {hinosComAudio && hinosComAudio.length > 0 && (
        <section className="py-14 md:py-20 px-4 bg-background">
          <div className="container mx-auto max-w-3xl">
            <motion.div className="text-center mb-10" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <motion.div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-5" initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
                <Headphones className="h-3.5 w-3.5 text-primary" />
                <span className="text-primary text-xs font-semibold tracking-wider uppercase">Com Áudio</span>
              </motion.div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'var(--font-serif)' }}>🎧 Hinos com Áudio</h2>
              <p className="text-muted-foreground mt-3">Ouça e toque junto com o áudio do hino.</p>
            </motion.div>

            <motion.div className="grid grid-cols-1 sm:grid-cols-2 gap-4" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              {hinosComAudio.map((hino) => (
                <motion.div key={hino.id} variants={itemVariants}>
                  <Link to={`/hino/${hino.id}`} className="group flex items-center gap-4 p-4 rounded-2xl bg-card border border-border hover:border-primary/30 hover:shadow-lg transition-all duration-300">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                      <Volume2 className="h-5 w-5 text-primary group-hover:text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors truncate">{hino.titulo}</h3>
                      <p className="text-muted-foreground text-xs mt-0.5">Hino nº {hino.numero} • Com áudio</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary shrink-0 transition-colors" />
                  </Link>
                </motion.div>
              ))}
            </motion.div>

            <div className="text-center mt-8">
              <Link to="/ouvir-hinos" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors">
                Ouvir todos os hinos <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ===== 8. HINOS RECENTES ===== */}
      <section className="py-14 md:py-20 px-4 bg-muted/50">
        <div className="container mx-auto max-w-3xl">
          <motion.div className="text-center mb-10" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <motion.div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-5" initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              <span className="text-primary text-xs font-semibold tracking-wider uppercase">Novidades</span>
            </motion.div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'var(--font-serif)' }}>🆕 Hinos Recentes</h2>
            <p className="text-muted-foreground mt-3">Últimos hinos adicionados à plataforma.</p>
          </motion.div>

          {!isLoading && recentHinos && recentHinos.length > 0 && (
            <motion.div className="grid grid-cols-1 sm:grid-cols-2 gap-4" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              {recentHinos.map((hino) => (
                <motion.div key={hino.id} variants={itemVariants}>
                  <Link to={`/hino/${hino.id}`} className="group flex items-center gap-4 p-4 rounded-2xl bg-card border border-border hover:border-primary/30 hover:shadow-lg transition-all duration-300">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                      <Music className="h-6 w-6 text-primary group-hover:text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors truncate">{hino.titulo}</h3>
                      <p className="text-muted-foreground text-xs mt-0.5">Hino nº {hino.numero}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary shrink-0 transition-colors" />
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* ===== 9. HISTÓRICO ===== */}
      {history.length > 0 && (
        <section className="py-14 md:py-20 px-4 bg-background">
          <div className="container mx-auto max-w-3xl">
            <motion.div className="text-center mb-10" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <motion.div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-5" initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
                <Eye className="h-3.5 w-3.5 text-primary" />
                <span className="text-primary text-xs font-semibold tracking-wider uppercase">Histórico</span>
              </motion.div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'var(--font-serif)' }}>Últimos Hinos Acessados</h2>
              <p className="text-muted-foreground mt-3">Continue de onde você parou.</p>
            </motion.div>

            <motion.div className="bg-card rounded-2xl overflow-hidden border border-border shadow-sm" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              {history.slice(0, 6).map((item, index) => (
                <motion.div key={item.id} variants={itemVariants}>
                  <Link to={`/hino/${item.id}`} className={`group flex items-center justify-between px-5 py-4 hover:bg-primary/5 transition-all duration-300 ${index !== Math.min(history.length, 6) - 1 ? 'border-b border-border' : ''}`}>
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <Music className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-foreground group-hover:text-primary transition-colors truncate">{item.titulo}</h3>
                        <p className="text-muted-foreground text-xs mt-0.5">Hino nº {item.numero}</p>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary shrink-0 transition-colors" />
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* ===== 10. CONTADORES ===== */}
      <section className="py-16 md:py-20 px-4 bg-[hsl(var(--navy))] relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[400px] h-[200px] bg-primary/5 rounded-full blur-[100px] pointer-events-none" />
        <div className="container mx-auto max-w-4xl relative z-10">
          <motion.div className="grid grid-cols-3 gap-4 md:gap-8" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            {[
              { value: hinoCountLabel, label: "Hinos Disponíveis", icon: <Music className="h-5 w-5" /> },
              { value: "📖", label: "Atualizações Semanais", icon: <BookOpen className="h-5 w-5" /> },
              { value: "100%", label: "Gratuito", icon: <Heart className="h-5 w-5" /> }
            ].map((item, i) => (
              <motion.div key={i} className="relative group rounded-2xl bg-gradient-to-b from-white/[0.07] to-white/[0.02] backdrop-blur-sm border border-white/[0.08] p-6 md:p-8 text-center transition-all duration-500 hover:border-primary/30" whileHover={{ scale: 1.03, y: -4 }} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.15 }}>
                <div className="absolute inset-0 rounded-2xl bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                <div className="relative z-10">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-4 text-primary">{item.icon}</div>
                  <span className="text-2xl md:text-4xl font-bold text-white block" style={{ fontFamily: 'var(--font-serif)' }}>{item.value}</span>
                  <p className="text-white/40 mt-2 text-xs md:text-sm font-medium tracking-wide">{item.label}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== SOBRE (SEO) ===== */}
      <section className="py-16 md:py-20 px-4 bg-muted">
        <div className="container mx-auto max-w-3xl text-center">
          <motion.article initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4" style={{ fontFamily: 'var(--font-serif)' }}>Sobre o Louve Hinos Avulsos</h2>
            <div className="w-16 h-px bg-primary/40 mx-auto mb-6" />
            <p className="text-muted-foreground leading-relaxed max-w-2xl mx-auto text-base md:text-lg mb-4">
              O <strong className="text-primary">Louve Hinos Avulsos</strong> é uma plataforma dedicada à organização de hinos avulsos com letras completas e cifras atualizadas. Nosso propósito é oferecer um ambiente simples, organizado e confiável para músicos, instrumentistas e todos que desejam louvar com excelência.
            </p>
            <p className="text-muted-foreground leading-relaxed max-w-2xl mx-auto text-base md:text-lg mb-8">
              Aqui você encontra hinos avulsos revisados, organizados e prontos para facilitar ensaios, estudos e momentos de louvor.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-lg mx-auto">
              {["Letras organizadas", "Cifras simplificadas", "Ferramentas para músicos"].map((item, i) => (
                <motion.div key={i} className="flex items-center gap-2 justify-center text-foreground" initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}>
                  <span className="text-primary">🔹</span> {item}
                </motion.div>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/hinos-avulsos" className="text-primary font-semibold hover:underline transition-colors text-base md:text-lg">Acesse os Hinos Avulsos Atualizados →</Link>
            </div>
          </motion.article>
        </div>
      </section>

      {/* ===== CTA FORTE ===== */}
      <section className="relative py-20 md:py-28 px-4 overflow-hidden">
        <img src={guitarColorfulBg} alt="Violão em ambiente de adoração" className="absolute inset-0 w-full h-full object-cover" loading="lazy" width={1920} height={1080} decoding="async" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80" />
        <div className="container mx-auto text-center max-w-2xl relative z-10">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-serif)' }}>📖 Quer aprender novos hinos toda semana?</h2>
            <p className="text-white/60 mb-8 text-lg">Receba atualizações e novos hinos direto no seu celular.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://wa.me/65999778086?text=Ol%C3%A1%21%20Gostaria%20de%20receber%20atualiza%C3%A7%C3%B5es%20do%20Louve." target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#20bd5a] transition-all duration-300 hover:scale-105 shadow-xl">
                <MessageCircle className="h-5 w-5" />Entrar no WhatsApp
              </a>
              <Link to="/hinos-avulsos" className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-lg text-lg font-semibold hover:bg-primary/90 transition-all duration-300 hover:scale-105 shadow-xl">
                <Music className="h-5 w-5" />Ver Hinos Agora
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== PROVA SOCIAL ===== */}
      <TestimonialsSection />

      {/* ===== FRASE BÍBLICA ===== */}
      <section className="relative py-16 md:py-24 px-4 overflow-hidden">
        <img src={guitarForestBg} alt="Mãos tocando violão à luz de velas" className="absolute inset-0 w-full h-full object-cover" loading="lazy" width={1920} height={1080} decoding="async" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/85" />
        <div className="container mx-auto text-center max-w-2xl relative z-10">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
            <Quote className="h-10 w-10 text-primary/50 mx-auto mb-4" />
            <p className="text-2xl md:text-4xl font-bold text-white text-shadow-lg italic leading-snug" style={{ fontFamily: 'var(--font-serif)' }}>"Cantai ao Senhor um cântico novo, cantai ao Senhor toda a terra."</p>
            <p className="text-white/50 mt-4 text-lg">Salmos 96:1</p>
          </motion.div>
        </div>
      </section>

      {/* ===== BLOG ===== */}
      {blogPosts && blogPosts.length > 0 && (
        <section className="py-14 md:py-20 px-4 bg-background">
          <div className="container mx-auto max-w-5xl">
            <motion.div className="text-center mb-10" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Newspaper className="h-10 w-10 text-primary mx-auto mb-4" />
              <h2 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'var(--font-serif)' }}>Blog Gospel</h2>
              <p className="text-muted-foreground mt-3 max-w-lg mx-auto">Devocionais, dicas musicais e reflexões para edificar sua caminhada.</p>
            </motion.div>
            <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }}>
              {blogPosts.slice(0, 3).map((post) => {
                const categoryLabel = BLOG_CATEGORIES.find(c => c.value === post.category)?.label || post.category;
                return (
                  <motion.div key={post.id} variants={itemVariants}>
                    <Link to={`/blog/${post.slug}`} className="group block bg-card rounded-2xl border border-border overflow-hidden shadow-sm hover:shadow-xl hover:border-primary/30 transition-all duration-300">
                      {post.cover_image_url ? (
                        <img src={post.cover_image_url} alt={post.title} className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      ) : (
                        <div className="w-full h-44 bg-muted flex items-center justify-center"><BookOpen className="h-12 w-12 text-muted-foreground/30" /></div>
                      )}
                      <div className="p-5">
                        <span className="text-xs text-primary font-medium">{categoryLabel}</span>
                        <h3 className="font-bold text-foreground mt-1 mb-2 line-clamp-2 group-hover:text-primary transition-colors" style={{ fontFamily: 'var(--font-serif)' }}>{post.title}</h3>
                        <p className="text-muted-foreground text-sm line-clamp-2">{post.excerpt}</p>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
            <div className="text-center mt-8">
              <Link to="/blog" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors">Ver todos os artigos <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </section>
      )}

      {/* ===== FAQ ===== */}
      <section className="py-14 md:py-20 px-4 bg-muted">
        <div className="container mx-auto max-w-2xl">
          <motion.div className="text-center mb-10" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <HelpCircle className="h-10 w-10 text-primary mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'var(--font-serif)' }}>Perguntas Frequentes</h2>
            <p className="text-muted-foreground mt-3">Tire suas dúvidas sobre o Louve.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
            <Accordion type="single" collapsible className="space-y-3">
              {FAQ_ITEMS.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="bg-card border border-border rounded-xl px-5 data-[state=open]:border-primary/30 data-[state=open]:shadow-md transition-all duration-300">
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:text-primary transition-colors py-4 text-sm md:text-base">{item.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed pb-4">{item.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* FAQ JSON-LD for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": FAQ_ITEMS.map(item => ({ "@type": "Question", "name": item.question, "acceptedAnswer": { "@type": "Answer", "text": item.answer } })) }) }} />

      <EmailCapture />
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Index;
